import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ApplicationResponse } from "@src/common/domain/common.model";
import api from "@src/common/services/api.service";
import AppConstants from "@src/utils/app.constants";
import { GanttStore } from "../domain/data.constant";

export const fetchAllDependencies = createAsyncThunk<
  any[],
  { projectId: string; type: string; isRevisionRequired?:boolean }
>("gantt/fetchAllDependencies", async ({ projectId, type, isRevisionRequired }) => {
  const url = `${AppConstants.api}/gantt/all-dependencies`;

  const response = await api.get<ApplicationResponse<any[]>>(url, {
    params: {
      projectId,
      projectType: type.toUpperCase(),
      isRevisionRequired
    },
  });
  return response.data.data || [];
});

export const fetchAllTasks = createAsyncThunk<
  any[],
  { projectId: string; type: string; isRevisionRequired?:boolean }
>("gantt/fetchAllTasks", async ({ projectId, type, isRevisionRequired = false }) => {
  const url = `${AppConstants.api}/gantt/all-tasks`;

  const response = await api.get<ApplicationResponse<any[]>>(url, {
    params: {
      projectId,
      projectType: type.toUpperCase(),
      isRevisionRequired
    },
  });
  return response.data.data || [];
});

export const fetchAllGanttEvents = createAsyncThunk<
  any[],
  { projectId: string; type: string }
>("gantt/fetchAllGanttEvents", async ({ projectId, type }) => {
  const url = `${AppConstants.api}/gantt/all-events`;

  const response = await api.get<ApplicationResponse<any[]>>(url, {
    params: {
      projectId,
      projectType: type.toUpperCase(),
    },
  });
  return response.data.data || [];
});

export const fetchAllGanttMilestones = createAsyncThunk<
  any[],
  { projectId: string; type: string }
>("gantt/fetchAllGanttMilestones", async ({ projectId, type }) => {
  const url = `${AppConstants.api}/gantt/all-milestones`;

  const response = await api.get<ApplicationResponse<any[]>>(url, {
    params: {
      projectId,
      projectType: type.toUpperCase(),
    },
  });
  return response.data.data || [];
});

const GanttSlice = createSlice({
  name: "gantt",
  initialState: {
    dependencyMinimal: { status: "loading", error: null, data: [] },
    taskMinimal: { status: "loading", error: null, data: [] },
    eventMinimal: { status: "loading", error: null, data: [] },
    milestoneMinimal: { status: "loading", error: null, data: [] },
  } as GanttStore,
  reducers: {
    resetGantt: (state) => {
      state.dependencyMinimal = { status: "loading", error: null, data: [] };
      state.taskMinimal = { status: "loading", error: null, data: [] };
      state.eventMinimal = { status: "loading", error: null, data: [] };
      state.milestoneMinimal = { status: "loading", error: null, data: [] };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllDependencies.pending, (state, action) => {
        state.dependencyMinimal = {
          status: "loading",
          data: [],
          error: null,
        };
      })
      .addCase(fetchAllDependencies.fulfilled, (state, action) => {
        state.dependencyMinimal = {
          status: "idle",
          data: action.payload,
          error: null,
        };
      })
      .addCase(fetchAllDependencies.rejected, (state, action) => {
        state.dependencyMinimal = {
          status: "failed",
          data: [],
          error: action.error,
        };
      })
      .addCase(fetchAllTasks.pending, (state, action) => {
        state.taskMinimal = {
          status: "loading",
          data: [],
          error: null,
        };
      })
      .addCase(fetchAllTasks.fulfilled, (state, action) => {
        state.taskMinimal = {
          status: "idle",
          data: action.payload,
          error: null,
        };
      })
      .addCase(fetchAllTasks.rejected, (state, action) => {
        state.taskMinimal = {
          status: "failed",
          data: [],
          error: action.error,
        };
      })
      .addCase(fetchAllGanttEvents.pending, (state, action) => {
        state.eventMinimal = {
          status: "loading",
          data: [],
          error: null,
        };
      })
      .addCase(fetchAllGanttEvents.fulfilled, (state, action) => {
        state.eventMinimal = {
          status: "idle",
          data: action.payload,
          error: null,
        };
      })
      .addCase(fetchAllGanttEvents.rejected, (state, action) => {
        state.eventMinimal = {
          status: "failed",
          data: [],
          error: action.error,
        };
      })
      .addCase(fetchAllGanttMilestones.pending, (state, action) => {
        state.milestoneMinimal = {
          status: "loading",
          data: [],
          error: null,
        };
      })
      .addCase(fetchAllGanttMilestones.fulfilled, (state, action) => {
        state.milestoneMinimal = {
          status: "idle",
          data: action.payload,
          error: null,
        };
      })
      .addCase(fetchAllGanttMilestones.rejected, (state, action) => {
        state.milestoneMinimal = {
          status: "failed",
          data: [],
          error: action.error,
        };
      });
  },
});
export const { resetGantt } = GanttSlice.actions;

export default GanttSlice.reducer;
